import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Breadcrumbs,
  Link,
  IconButton,
  InputAdornment,
  Alert,
  Snackbar,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import {
  EditNote as EditNoteIcon,
  DeleteOutline as DeleteOutlineIcon,
  Visibility,
  VisibilityOff,
  NavigateNext as NavigateNextIcon,
} from "@mui/icons-material";
import { userSettingService } from "../services/userSettingService";
import { authService } from "../../../features/auth/services/authService";
type UserSettingProps = {
  children?: React.ReactNode;
};


const SettingHeader = () => (
  <Typography variant="h3" align="center" sx={{ mb: 6, mr:50, fontFamily: 'Nandia' }}>
    Setting
  </Typography>
);

const MainView = ({ setView, onAction, userEmail }: any) => (
  <Box>
    <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, fontFamily: 'Nandia' }}>
      User Setting
    </Typography>
    <Stack spacing={4}>
      <Box>
        <Typography variant="body2" sx={{ mb: 1, color: "#666" }}>
          E-mail
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, maxWidth: 500}}>
          <TextField
            fullWidth
            value={userEmail}
            disabled
            sx={inputDisableStyle}
          />
          <Button
            startIcon={<EditNoteIcon />}
            sx={{ color: "#666" }}
            // onClick={() => setView("sent-email")}
            onClick={() => onAction("email")}
          >
            Change
          </Button>
        </Box>
      </Box>
      <Box>
        <Typography variant="body2" sx={{ mb: 1, color: "#666" }}>
          Password
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, maxWidth: 500 }}>
          <TextField
            fullWidth
            type="password"
            value="********"
            disabled
            sx={inputDisableStyle}
          />
          <Button
            startIcon={<EditNoteIcon />}
            sx={{ color: "#666" }}
            // onClick={() => setView("sent-password")}
            onClick={() => onAction("password")}
          >
            Change
          </Button>
        </Box>
      </Box>
      <Button
        onClick={() => setView("delete-account")}
        startIcon={<DeleteOutlineIcon />}
        sx={{ color: "#666", width: "fit-content" }}
      >
        Delete account
      </Button>
    </Stack>
  </Box>
);

const SentEmailView = ({
  setView,
  type,
  onResend,
  userEmail,
}: {
  setView: any;
  type: "email" | "password";
  onResend: (type: "email" | "password") => Promise<void>;
  userEmail: string;
}) => {
  // ตั้งค่าเริ่มต้น 60 วิ ทันทีที่เข้ามาหน้านี้ (เพราะเมลถูกส่งมาแล้วรอบนึง)
  const [countdown, setCountdown] = useState(60);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  const handleResend = async () => {
    if (countdown === 0) {
      await onResend(type);
      setCountdown(60); // Reset กลับไป 60 วิใหม่หลังจากกด Resend สำเร็จ
    }
  };

  return (
    <Box>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 4 }}>
        <Link
          underline="none"
          onClick={() => setView("main")}
          sx={{ cursor: "pointer", color: "#999" }}
        >
          User Setting
        </Link>
        <Typography sx={{ fontWeight: 700, color: "#000" }}>
          {type === "email" ? "Change E-mail" : "Change Password"}
        </Typography>
      </Breadcrumbs>

      <Typography sx={{ mb: 4 }}>
        {type === "email"
          ? "A verification email has been sent to your current e-mail to confirm the change."
          : "A password reset link has been sent to your email address."}
      </Typography>

      <TextField fullWidth value={userEmail} disabled sx={inputDisableStyle} />

      <Typography variant="body2" sx={{ mt: 4 }}>
        If you have not received the email, please{" "}
        {countdown > 0 ? (
          // สถานะ Disable: แสดงตัวเลขถอยหลังและเปลี่ยนสีเป็นสีเทา
          <Box component="span" sx={{ color: "#999", fontWeight: 700, cursor: "not-allowed" }}>
            click here ({countdown}s)
          </Box>
        ) : (
          // สถานะ Enable: กลับมาเป็น Link สีดำที่กดได้
          <Link
            underline="none"
            onClick={handleResend}
            sx={{ cursor: "pointer", fontWeight: 700, color: "#000", "&:hover": { textDecoration: 'underline' } }}
          >
            click here
          </Link>
        )}
        {" "}to receive a new one.
      </Typography>
    </Box>
  );
};

const ResetPasswordView = ({
  passwords,
  setPasswords,
  showPass,
  setShowPass,
  btnStyle,
  onConfirm,
  onBack,
}: any) => {
  return (
    <Box>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 4 }}
      >
        <Link
          underline="none"
          onClick={onBack}
          sx={{ cursor: "pointer", color: "#999" }}
        >
          User Setting
        </Link>
        <Typography sx={{ fontWeight: 700, color: "#000" }}>
          Change Password
        </Typography>
      </Breadcrumbs>

      <Typography sx={{ mb: 3 }}>Please enter your new password.</Typography>

      <Stack spacing={2.5}>
        {["current", "new", "confirm"].map((key) => (
          <TextField
            key={key}
            fullWidth
            value={passwords[key]}
            onChange={(e) =>
              setPasswords({ ...passwords, [key]: e.target.value })
            }
            type={showPass[key] ? "text" : "password"}
            placeholder={key + " password"}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowPass({
                          ...showPass,
                          [key]: !showPass[key as keyof typeof showPass],
                        })
                      }
                    >
                      {showPass[key as keyof typeof showPass] ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            sx={inputStyle}
          />
        ))}
        <Button variant="contained" onClick={onConfirm} sx={btnStyle}>
          Confirm
        </Button>
      </Stack>
    </Box>
  );
};

const ChangeEmailView = ({
  newEmail,
  setNewEmail,
  confirmPass,
  setConfirmPass,
  showPass,
  setShowPass,
  onConfirm,
  onBack,
}: any) => {
  return (
    <Box>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 4 }}
      >
        <Link
          underline="none"
          onClick={onBack}
          sx={{ cursor: "pointer", color: "#999" }}
        >
          User Setting
        </Link>
        <Typography sx={{ fontWeight: 700, color: "#000" }}>
          Change E-mail
        </Typography>
      </Breadcrumbs>

      <Typography sx={{ mb: 3 }}>
        Please enter your new email address and password to proceed with
        confirmation.
      </Typography>

      <Stack spacing={2.5}>
        {/* ช่องกรอก Email ใหม่ */}
        <TextField
          fullWidth
          placeholder="New Email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          sx={inputStyle}
        />

        {/* ช่องกรอก Password เพื่อยืนยัน */}
        <TextField
          fullWidth
          type={showPass ? "text" : "password"}
          placeholder="Password"
          value={confirmPass}
          onChange={(e) => setConfirmPass(e.target.value)}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPass(!showPass)}>
                    {showPass ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          sx={inputStyle}
        />

        <Button variant="contained" onClick={onConfirm} sx={btnStyle}>
          Confirm
        </Button>
      </Stack>
    </Box>
  );
};

const DeleteAccountView = ({
  deletePass,
  setDeletePass,
  showPass,
  setShowPass,
  confirmText,
  setConfirmText,
  onDelete,
  onBack,
}: any) => {
  return (
    <Box>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 4 }}
      >
        <Link
          underline="none"
          onClick={onBack}
          sx={{ cursor: "pointer", color: "#999" }}
        >
          User Setting
        </Link>
        <Typography sx={{ fontWeight: 700, color: "#000" }}>
          Delete Account
        </Typography>
      </Breadcrumbs>

      <Typography sx={{ mb: 3 }}>
        Please Enter your password and type “Delete” to confirm to delete your
        account
      </Typography>

      <Stack spacing={2.5}>
        <TextField
          fullWidth
          type={showPass ? "text" : "password"}
          placeholder="Enter your password"
          value={deletePass}
          onChange={(e) => setDeletePass(e.target.value)}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPass(!showPass)}>
                    {showPass ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          sx={inputStyle}
        />

        <TextField
          fullWidth
          placeholder="Type Delete to confirm"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          sx={inputStyle}
        />

        <Button
          variant="contained"
          onClick={onDelete}
          sx={{
            ...btnStyle,
            bgcolor: "#1a1a1a",
            "&:hover": { bgcolor: "#333" },
          }}
        >
          Delete
        </Button>
      </Stack>
    </Box>
  );
};

const UserSetting: React.FC<UserSettingProps> = () => {
  const [params, setParams] = useState({ key: "", login: "", token: "" });
  const [userEmail, setUserEmail] = useState("");
  useEffect(() => {
    const savedEmail = localStorage.getItem('email') || "Admin1234@gmail.com";
    setUserEmail(savedEmail);

    const queryParams = new URLSearchParams(window.location.search);
    const mode = queryParams.get("mode");
    const key = queryParams.get("key") || '';
    const login = queryParams.get("login") || '';
    const token = queryParams.get("token") || '';
    if (key && login) {
      setParams({ key, login, token });
    }

    if (mode === "change-email" || mode === "reset-password") {
      setView(mode);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  const [view, setView] = useState<
    | "main"
    | "sent-email"
    | "change-email"
    | "sent-password"
    | "reset-password"
    | "delete-account"
  >("main");

  const [showPass, setShowPass] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [passwords, setPasswords] = useState({
    current: "",
    new: "",
    confirm: "",
  });
  const [newEmail, setNewEmail] = useState("");
  const [confirmPassForEmail, setConfirmPassForEmail] = useState("");
  const [showPassEmail, setShowPassEmail] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [severity, setSeverity] = useState<"success" | "error">("success");
  const [deletePass, setDeletePass] = useState("");
  const [showDeletePass, setShowDeletePass] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const [isSending, setIsSending] = useState(false);

  const handleRequestChange = async (type: "email" | "password") => {
    setIsSending(true); // Backdrop จะโชว์ทันที
    try {
      const payload = { email: userEmail }; 
      await userSettingService.sendEmail(payload, type);
      
      setView(type === "email" ? "sent-email" : "sent-password");
    } catch (error: any) {
      const errorData = error.response?.data;
      const msg = errorData?.details?.message || error.message || "Failed to send email";
      showAlert(msg, "error");
    } finally {
      setIsSending(false); 
    }
  };

  const handleConfirmReset = async () => {
    if (!passwords.new || !passwords.confirm) {
      showAlert("Please fill in all password fields.", "error");
      return;
    }
    if (passwords.new !== passwords.confirm) {
      showAlert("New passwords do not match.", "error");
      return;
    }
    setIsSending(true);
    try {
      const result = await authService.resetPassword({
        key: params.key,
        login: params.login,
        password: passwords.new
      });
      handleConfirmSuccess(result.message || "Password reset successfully."); 
    } catch (error: any) {
      const errorData = error.response?.data;
      const msg = errorData?.details?.message || error.message || "Failed to reset password";
      showAlert(msg, "error");
    } finally {
      setIsSending(false);
    }
  };

  const handleConfirmChangeEmail = async () => {
    if (!newEmail || !confirmPassForEmail) {
      showAlert("Please enter both new email and password.", "error");
      return;
    }
    if (!newEmail.includes("@")) {
      showAlert("Please enter a valid email address.", "error");
      return;
    }
    setIsSending(true);
    try {
      await userSettingService.changeEmail({
        token: params.token, 
        new_email: newEmail,
        current_password: confirmPassForEmail
      });
      localStorage.setItem('email', newEmail);
      setUserEmail(newEmail)
      handleConfirmSuccess("Your email address has been changed."); 
    } catch (error: any) {
      const errorData = error.response?.data;
      const msg = errorData?.details?.message || error.message || "Failed to change email";
      showAlert(msg, "error");
    } finally {
      setIsSending(false);
    }
  };

  const handleBackToMain = () => {
    setPasswords({ current: "", new: "", confirm: "" });
    setShowPass({ current: false, new: false, confirm: false });
    setNewEmail("");
    setConfirmPassForEmail("");
    setShowPassEmail(false);
    setView("main");
    setDeletePass("");
    setDeleteConfirmText("");
    setShowDeletePass(false);
  };

  const showAlert = (message: string, type: "success" | "error") => {
    setSnackbarMsg(message);
    setSeverity(type);
    setOpenSnackbar(true);
  };

  const handleConfirmSuccess = (message: string) => {
    showAlert(message, "success");
    handleBackToMain();
  };

  return (
    <Box sx={{ mx: "auto", mt: 4 }}>
      <SettingHeader />
      {view === "main" && <MainView 
        setView={setView} onAction={handleRequestChange} userEmail={userEmail} />
      }
      {view === "sent-email" && (
        <SentEmailView 
          setView={setView} 
          type="email" 
          onResend={handleRequestChange} // ฟังก์ชันที่มี setIsSending(true/false) และยิง API
          userEmail={userEmail}
        />
      )}
      {view === "change-email" && (
        <ChangeEmailView
          newEmail={newEmail}
          setNewEmail={setNewEmail}
          confirmPass={confirmPassForEmail}
          setConfirmPass={setConfirmPassForEmail}
          showPass={showPassEmail}
          setShowPass={setShowPassEmail}
          onConfirm={handleConfirmChangeEmail}
          onBack={handleBackToMain}
        />
      )}
      {view === "sent-password" && (
        <SentEmailView 
          setView={setView} 
          type="password" 
          onResend={handleRequestChange} 
          userEmail={userEmail}
        />
      )}
      {view === "reset-password" && (
        <ResetPasswordView
          passwords={passwords}
          setPasswords={setPasswords}
          showPass={showPass}
          setShowPass={setShowPass}
          setView={setView}
          btnStyle={btnStyle}
          onConfirm={handleConfirmReset}
          onBack={handleBackToMain}
        />
      )}
      {view === "delete-account" && (
        <DeleteAccountView
          deletePass={deletePass}
          setDeletePass={setDeletePass}
          showPass={showDeletePass}
          setShowPass={setShowDeletePass}
          confirmText={deleteConfirmText}
          setConfirmText={setDeleteConfirmText}
          onBack={handleBackToMain}
          onDelete={() => {
            if (!deletePass) {
              showAlert("Please enter your password.", "error");
            } else if (deleteConfirmText !== "Delete") {
              showAlert(
                "Please type 'Delete' (case-sensitive) to confirm.",
                "error",
              );
            } else {
              // Logic สำหรับลบไอดี (เช่น เรียก API)
              showAlert("Your account has been deleted.", "success");
              handleBackToMain();
            }
          }}
        />
      )}

      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2
        }}
        open={isSending} // ใช้ตัวแปรเดียวกับที่คุณทำ API Call
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6">Sending email, please wait...</Typography>
      </Backdrop>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={severity} // ✅ จะเปลี่ยนเป็น success (เขียว) หรือ error (แดง) อัตโนมัติ
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: "10px",
            boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
            bgcolor: severity === "success" ? "#e8f5e9" : "#ffebee",
            color: severity === "success" ? "#2e7d32" : "#c62828",
            "& .MuiAlert-icon": {
              color: severity === "success" ? "#4caf50" : "#ef5350",
            },
          }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};
const inputDisableStyle = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "#f5f5f5",
    borderRadius: "10px",
    "& fieldset": { border: "none" },
  },
  maxWidth: 500
};

const inputStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
  },
  maxWidth: 500
};
const btnStyle = {
  bgcolor: "#1a1a1a",
  color: "#fff",
  textTransform: "none",
  borderRadius: "10px",
  py: 1.5,
  width: "200px",
};
export default UserSetting;
